import React, { useEffect, useState, useRef } from 'react';
import { TypingMessage } from './TypingMessage';
import { DemoSidebar } from './DemoSidebar';
import { ChatMessage } from '../chat/ChatMessage';
import { ChatTypingIndicator } from '../chat/ChatTypingIndicator';
import { Message as ChatMessageType } from '../../hooks/useWebSocketChat';


export enum TourContext {
  GuestTour = 'guest-tour',
  NewUserTour = 'new-user-tour',
}

interface DemoMessage {
  text: string;
  isHuman: boolean;
  message_type?: string;
  customPauseBefore?: number;
  finishedTyping?: boolean;
}

interface Stage {
  explanation: string;
  messages: DemoMessage[];
  highlights: string[]
}

interface DemoScript {
  [key: string]: Stage;
}
const RESEARCH_REPORT_TEXT = JSON.stringify(
  {
    'introduction': "# Fan-Favorite Thai Restaurants in Chicago: A Culinary Exploration  \n\nChicago's Thai food scene has evolved into a \
vibrant tapestry of regional flavors, offering diners a chance to explore the diverse culinary traditions of Thailand. From the fiery, \
herbaceous dishes of Northern Thailand to the sweeter, coconut-infused flavors of the South, the city's Thai restaurants go far beyond \
the familiar pad Thai and green curry. According to insights from [Eater Chicago](https://chicago.eater.com/maps/best-thai-restaurants-chicago),\
this evolution has been driven by a growing appreciation for regional authenticity and creative interpretations of Thai cuisine.  \n\nIn recent years, \
the pandemic reshaped the dining landscape, with many Thai restaurants pivoting to carryout and delivery services. Despite these challenges, establishments \
across Chicago have continued to thrive, offering everything from tasting menus to casual street food-inspired dishes. Notable spots like Talard Thai Market \
and Paula's Thai Kitchen have gained recognition for their unique offerings, while newcomers like \
[Eat Fine Design by Khun Kung](https://chicago.eater.com/maps/best-thai-restaurants-chicago) have introduced innovative dishes such as southern-style Thai fried \
chicken and DIY papaya salad.  \n\nThis report delves into the fan-favorite Thai restaurants in Chicago, as highlighted by the passionate food enthusiasts on \
[r/chicagofood](https://www.reddit.com/r/chicagofood/). By combining community-driven recommendations with expert insights, we aim to provide a comprehensive guide \
to the city's must-visit Thai dining destinations. Whether you're a seasoned Thai food lover or a curious newcomer, Chicago's Thai restaurant scene promises an \
unforgettable culinary journey.",
    'conclusion': "## Conclusion\n\nChicago's Thai food scene has flourished into a dynamic and diverse culinary landscape, driven by a growing \
appreciation for regional authenticity, innovative fusion, and accessibility through carryout and delivery services. The r/chicagofood \
subreddit highlights several fan-favorite establishments that exemplify these trends. Longstanding institutions like \
[Opart Thai House](https://chicago.eater.com/maps/best-thai-restaurants-chicago) continue to deliver classic, high-quality dishes, while \
newer spots such as [Eat Fine Design by Khun Kung](https://chicago.eater.com/maps/best-thai-restaurants-chicago) push boundaries with creative \
offerings and influences from neighboring cuisines like Laotian and Vietnamese. Meanwhile, hidden gems like \
[Talard Thai Market](https://chicago.eater.com/maps/best-thai-restaurants-chicago) and neighborhood favorites like \
[Paula's Thai Kitchen](https://chicago.eater.com/maps/best-thai-restaurants-chicago) provide authentic, approachable dining experiences \
that resonate with locals.\n\nThe evolution of Thai cuisine in Chicago reflects broader culinary trends, including a shift toward regional \
specialties and the integration of Southeast Asian flavors. These changes not only cater to the city's adventurous diners but also highlight \
the interconnectedness of global food cultures. As the Thai food scene continues to grow, Chicagoans can expect even more innovative dishes \
and dining formats that celebrate the richness of Thai culinary traditions. For food enthusiasts, this presents an exciting opportunity to \
explore both familiar and novel flavors, ensuring that Thai cuisine remains a vital and beloved part of Chicago's vibrant dining culture.",
    'body': "# Fan-Favorite Thai Restaurants in Chicago According to r/chicagofood\n\n## Introduction\n\nChicago, \
a city known for its vibrant culinary scene, has embraced Thai cuisine with open arms. Over the years, the city's \
Thai food offerings have evolved significantly, moving beyond the traditional homestyle dishes that many Americans \
initially gravitated toward. Today, Chicago boasts a diverse array of Thai restaurants, showcasing regional specialties \
from Northern and Central Thailand and even incorporating influences from neighboring cuisines such as Laotian and Vietnamese.\
This report delves into fan-favorite Thai restaurants in Chicago, as highlighted by the r/chicagofood subreddit, using insights \
from the provided information and other reliable sources.\n\n---\n\n## The Evolution of Thai Cuisine in Chicago\n\nThai cuisine in \
Chicago has undergone a notable transformation in recent years. Historically, many Thai restaurants in the city focused on \
familiar dishes such as pad Thai and green curry, catering to American palates. However, a shift toward regional authenticity \
has brought a broader spectrum of flavors and dishes to the forefront. Restaurants now feature specialties from Northern \
Thailand, such as spicy larb and khao soi, as well as sweeter dishes from Southern Thailand \
([Eater Chicago, 2025](https://chicago.eater.com/maps/best-thai-restaurants-chicago)).\n\nThis evolution has been driven by a \
combination of factors, including increased awareness of Thai regionality, a growing demand for diverse dining experiences, and \
the creativity of local chefs. The pandemic also played a role, as many restaurants pivoted to carryout and delivery services, making \
Thai food more accessible to a wider audience.\n\n---\n\n## Fan-Favorite Thai Restaurants in Chicago\n\n### 1. **Talard Thai \
Market**\nLocated in Chicago, Talard Thai Market is a hidden gem that has garnered a loyal following. While primarily a market, it \
features a food counter in the back that serves authentic Thai dishes. This spot is celebrated for its unpretentious atmosphere and \
high-quality food, making it a favorite among locals seeking an authentic Thai dining experience \
([Eater Chicago, 2025](https://chicago.eater.com/maps/best-thai-restaurants-chicago)).\n\n### 2. **Paula's Thai \
Kitchen**\nSituated at 2441 N Halsted St in Lincoln Park, Paula's Thai Kitchen is renowned for its extensive menu, which includes both \
conventional and \"secret\" offerings. Fan favorites include the papaya salad topped with pork belly, crispy rice with sausage, and \
grilled meatballs. The restaurant also offers outdoor seating, making it a popular choice for diners looking to enjoy their meals al fresco. \
Paula's Thai Kitchen has consistently received praise for its flavorful dishes and welcoming \
ambiance ([Eater Chicago, 2025](https://chicago.eater.com/maps/best-thai-restaurants-chicago)). \n### 3. **Eat Fine Design by \
Khun Kung**\nA newcomer to Chicago's Thai food scene, Eat Fine Design by Khun Kung opened in 2023 at 2062 North Damen Avenue in Bucktown. \
This restaurant has quickly gained a reputation for its unique offerings, including southern-style Thai fried chicken, DIY papaya salad, and \
duck rolls. The menu also features hints of Laotian and Vietnamese influences, setting it apart from more traditional Thai establishments. \
Diners are encouraged to preorder their meals, ensuring a seamless and personalized dining experience \
([Eater Chicago, 2025](https://chicago.eater.com/maps/best-thai-restaurants-chicago)).\n\n### 4. **Opart Thai House - West Town**\nA \
longstanding favorite among Chicagoans, Opart Thai House has been serving authentic Thai cuisine for decades. Its West Town location at 1546 \
West Chicago Avenue continues to attract diners with classic dishes such as pad kra praow (stir-fried basil) and pad prik king (stir-fried curry). \
The \"tiger cry,\" a charbroiled beef dish served with a house-made spicy sauce, is a must-try. Opart Thai House's consistency and dedication to \
quality have solidified its status as a beloved institution in Chicago's Thai food scene \
([Eater Chicago, 2025](https://chicago.eater.com/maps/best-thai-restaurants-chicago)).\n\n---\n\n## Key Trends in Chicago's Thai \
Food Scene\n\n### 1. **Regional Specialties**\nAs mentioned earlier, Chicago's Thai restaurants have embraced regionality, offering \
dishes from Northern and Central Thailand that go beyond the standard fare. This trend reflects a growing appreciation for the diversity \
of Thai cuisine and a desire to introduce diners to new flavors and textures.\n\n### 2. **Carryout and Delivery**\nThe COVID-19 pandemic \
prompted many Thai restaurants to shift their focus to carryout and delivery services. While some establishments have reopened their dining rooms, \
others have chosen to remain strictly takeout, catering to the convenience-driven preferences of modern diners \
([Eater Chicago, 2025](https://chicago.eater.com/maps/best-thai-restaurants-chicago)).\n\n### 3. **Fusion and Innovation**\nRestaurants like \
Eat Fine Design by Khun Kung exemplify the fusion trend, incorporating elements of Laotian and Vietnamese cuisines into their menus. \
This approach not only broadens the appeal of Thai food but also highlights the interconnectedness of Southeast Asian culinary traditions.\n\n---\n\n## References\n\nAshok Selvam. (2025, February 24). Chicago's Best Thai Food - Eater \
Chicago. Eater Chicago. [https://chicago.eater.com/maps/best-thai-restaurants-chicago](https://chicago.eater.com/maps/best-thai-restaurants-chicago)",
    'sources': [
      {'url': 'https://www.reddit.com/r/chicagofood/comments/162b9xr/best_thai_places/',
      'title': ''},
      {'url': 'https://www.reddit.com/r/chicagofood/comments/sjz3bo/thai_restaurants_recommendations_needed/',
      'title': 'thai restaurants recommendations'},
      {'url': 'https://www.reddit.com/r/chicagofood/comments/qq98n1/weekly_suggestion_thread_thai_food/',
      'title': 'weekly suggestion thread thai food'},
      {'url': 'https://www.reddit.com/r/chicagofood/comments/xe6jdc/best_place_to_get_thai_food/',
      'title': 'best place to get thai food'},
      {'url': 'https://www.reddit.com/r/chicagofood/comments/12a0fq3/thai_food/',
      'title': 'thai food'},
      {'url': 'https://www.reddit.com/r/chicagofood/comments/16k6i3h/best_thai_food_in_chicago/',
      'title': 'best thai food in chicago'},
      {'url': 'https://www.reddit.com/r/chicagofood/comments/z79dqb/where_can_i_get_good_thai_food/',
      'title': 'where can i get good thai food'},
      {'url': 'https://www.reddit.com/r/chicago/comments/1f772t/looking_for_the_best_thai_restaurant_in_the_city/',
      'title': 'looking for the best thai restaurant in the city'},
      {'url': 'https://chicago.eater.com/maps/best-thai-restaurants-chicago',
      'title': "Chicago's Best Thai Food - Eater Chicago"}
    ]
  }
)


const DEMO_SCRIPT: DemoScript = {
  welcome: {
    explanation: "Welcome to Scientia! This tour will show you how your personal AI assistant works. We'll start with basic introductions.",
    messages: [
      {
        customPauseBefore: 4000,
        text: "## Hello and welcome!\n\nWe here at Scientia are here to help you stay connected to the world around you – where you live, the activities you love, and the things you do.",
        message_type: 'markdown',
        isHuman: false,
      },
      {
        text: "Before we dive in, do you have any questions?",
        customPauseBefore: 2500,
        isHuman: false,
      },
      {
        customPauseBefore: 2000,
        text: "Nice! \n\nSo... can you tell me more about what to expect?",
        isHuman: true,
      },
      {
        text: "I'm glad you asked!\n\nThrough natural conversation, your assistant will learn about you, your interests, and regular activities.",
        message_type: "markdown",
        isHuman: false,
      },
      {
        text: "When chatting with the assistant here, you can ask questions, ask your assistant to remind you of something, or ask it to periodically search the web for information. For example: \n\n&ensp; 'Every wednesday can you tell me what movies are playing in theaters near me?' or... \n\n &ensp; 'Can you let me know what bands are playing at The Metro every month?'",
        message_type: "markdown",
        isHuman: false,
      },
      {
        text: "Each morning, your assistant crafts a personalized email for you – pulling information from the web to proactively present information you will appreciate.",
        message_type: "markdown",
        isHuman: false,
      },
      {
        text: "Ok! So, I talk with my assistant, it learns about me...  Then every day, it spontaneously sends me an email with updates/reminders/internet search summaries based on what I'm interested in?",
        isHuman: true,
      },
      {
        text: "Exactly! \n\nAlso, with each morning message you will be able to view a more detailed report, and to chat with your assistant to further customize your experience.",
        message_type: "markdown",
        isHuman: false,
      },
      {
        text: "I'll show you an example morning message next – press the continue button below when you're ready!",
        message_type: "markdown",
        isHuman: false,
      }
    ],
    highlights: [
      'Customize your assistant through natural conversation.',
      "Stay connected to your interests and what's going on where you live."
    ],
  },
  daily_insight: {
    explanation: "Every day, Scientia sends you a personalized email with your local weather, calendar events, and information relevant to your interests. Here's an example of what that looks like:",
    messages: [
      {
        text: `
**Chicago Weather Update**

Today brings steady overcast skies and mild temperatures ranging from 54°F this morning up to 66°F by early afternoon. Winds will be moderate, mostly from the north and northeast, with humidity hovering between 62–80%. No precipitation is expected, so it's a comfortable day to be out and about—just be mindful of the breeze if you're planning a walk or a run.

---
**Today's Schedule**:\n- 9:00 AM: Team standup\n- 2:30 PM: Doctor appointment

**Quote of the Day**: \"Ain't nothin' to it but to do it.\" - Maya Angelou"

---

**Spotlight: Museum of Contemporary Art Chicago – June 2025 Preview**

If you're looking for inspiration, the MCA is rolling out a standout lineup for June:

- **Paul Pfeiffer: Prologue to the Story of the Birth of Freedom** (May 3 – August 31)
  - A timely exhibition exploring sports spectacle and media through striking installations.

- **Pipilotti Rist: Supersubjektiv** (through September 14)
  - Immersive multimedia environments—ideal if you're in the mood for sensory art experiences.

- **Wafaa Bilal: Indulge Me** (through October 19)
  - Interactive works delving into identity, displacement, and technology.

- **2025 Art Auction** (May 30 – June 6)
  - A rare event (only every five years!) featuring over 100 works by leading contemporary artists. Public viewing runs all week, with the live auction on June 6 if you're interested in seeing the city's art community at its most vibrant.

- **Tuesdays on the Terrace** returns June 24, launching a summer series of free outdoor concerts on the MCA's terrace. This year's lineup goes beyond jazz, incorporating blues, soul, and more—an ideal after-work option or a relaxed evening out.

- Additional June programs include art screenings, talks (like "Politics of Poetics" on June 21), and creative youth workshops.

You can find all details and reserve spots at [mcachicago.org](https://mcachicago.org/).

---

If you'd like updates about specific exhibitions, concerts, or other happenings—at the MCA or elsewhere in the city—just let me know. Have an excellent day, Stephan.

– Alfred
        `,
        isHuman: false,
        message_type: "markdown",
      },
      {
        customPauseBefore: 8000,
        text: "Nice – Can I customize what appears in my daily message?",
        isHuman: true,
      },
      {
        text: "Absolutely! During onboarding, you'll be able to tell me what information you'd like included in your daily insights.",
        isHuman: false
      },
      {
        text: "For example, you could say \"Let me know if today's weather will be good for outdoor activities\" or \"Please remove the quote of the day from my daily insights.\"",
        isHuman: false,
      },
      {
        customPauseBefore: 4000,
        text: "Before we jump into onboarding, there's one more thing I'd like to show you! When you're ready, press the continue button below.",
        isHuman: false
      }
    ],
    highlights: [
      'Daily messages include the weather, upcoming calendar event reminders, and insightful information relevant to you.',
      'Seamlessly customize your experience through natural conversation.'
    ],
  },
  research: {
    explanation: "When you ask about topics that require current information, Scientia can search the web and provide detailed research results.",
    messages: [
      {
        text: "When you are on the website, you can ask your assistant to search the web for information about a topic – even specifying the website to search!",
        isHuman: false
      },
      {
        text: "For example, you could say \"What are some fan-favorite thai restaurants in Chicago according to the subreddit r/chicagofood?\"",
        isHuman: false
      },
      {
        text: "I'll do some digging and return the results like so:",
        isHuman: false
      },
      {
        customPauseBefore: 8000,
        isHuman: false,
        text: "# Fan-Favorite Thai Restaurants in Chicago: A Culinary Exploration  \n\nChicago\'s Thai food scene has evolved into a vibrant tapestry of regional \
flavors, offering diners a chance to explore the diverse culinary traditions of Thailand. From the fiery, herbaceous dishes of Northern Thailand to the sweeter, \
coconut-infused flavors of the South, the city\'s Thai restaurants go far beyond the familiar pad Thai and green curry. According to insights from [Eater Chicago](https://chicago.eater.com/maps/best-thai-restaurants-chicago), \
this evolution has been driven by a growing appreciation for regional authenticity and creative interpretations of Thai cuisine.  \n\nIn recent years, the pandemic reshaped the dining landscape, with many Thai restaurants pivoting \
to carryout and delivery services. Despite these challenges, establishments across Chicago have continued to thrive, offering everything from tasting menus to casual street food-inspired dishes. Notable spots like Talard Thai Market \
and Paula\'s Thai Kitchen have gained recognition for their unique offerings, while newcomers like [Eat Fine Design by Khun Kung](https://chicago.eater.com/maps/best-thai-restaurants-chicago) have introduced innovative dishes such as \
southern-style Thai fried chicken and DIY papaya salad.  \n\nThis report delves into the fan-favorite Thai restaurants in Chicago, as highlighted by the passionate food enthusiasts on [r/chicagofood](https://www.reddit.com/r/chicagofood/). \
By combining community-driven recommendations with expert insights, we aim to provide a comprehensive guide to the city's must-visit Thai dining destinations. Whether you're a seasoned Thai food lover or a curious newcomer, Chicago's Thai \
restaurant scene promises an unforgettable culinary journey.",
        message_type: 'markdown',
      },
      {
        isHuman: false,
        message_type: "research_report",
        customPauseBefore: 3000,
        text: RESEARCH_REPORT_TEXT,
      },
    ],
    highlights: [
      'Get real-time information from web searches about topics you care about.',
      'Access detailed reports with sources and summaries.'
    ],
  },
  wrap_up: {
    explanation: "Now that you've seen how Scientia works, it's time to set up your own personalized experience through the onboarding process.",
    highlights: [],
    messages: [
      {
        text: "That covers the main features of Scientia!\n\nClick continue to kick things off.",
        isHuman: false
      },
    ],
  }
};

// Add useMediaQuery hook with proper error handling
const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    try {
      const media = window.matchMedia(query);

      setMatches(media.matches);

      const listener = (event: MediaQueryListEvent) => {
        setMatches(event.matches);
      };

      if (media.addEventListener) {
        media.addEventListener('change', listener);
        return () => media.removeEventListener('change', listener);
      } else {
        // Fallback for older browsers
        media.addListener(listener);
        return () => media.removeListener(listener);
      }
    } catch (error) {
      // Log error but don't throw - gracefully degrade to desktop layout
      console.warn('Error setting up media query listener:', error);
      return;
    }
  }, [query]); // Remove 'matches' dependency to prevent infinite re-renders

  return matches;
};

export const DemoTourChat: React.FC<{ context: TourContext }> = ({ context = TourContext.NewUserTour }) => {
  const [messages, setMessages] = useState<DemoMessage[]>([]);
  const [currentStage, setCurrentStage] = useState<string>("welcome");

  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isAssistantTyping, setIsAssistantTyping] = useState(true); // Start with typing indicator
  const [completedHighlights, setCompletedHighlights] = useState<string[]>([]);
  const [humanReadTimeDelay, setHumanReadTimeDelay] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery('(max-width: 768px)'); // must use px value instead of variable

  const stages = Object.keys(DEMO_SCRIPT);
  const currentStageIndex = stages.indexOf(currentStage);
  const totalStages = stages.length;

  useEffect(() => {
    if (!isMobile) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.filter(message => message.message_type !== "research_report")]);

  // Handle message transitions
  useEffect(() => {
    if (currentMessageIndex < 0) return;

    const currentStageMessages = DEMO_SCRIPT[currentStage].messages;
    if (currentMessageIndex >= currentStageMessages.length) {
      setIsAssistantTyping(false);
      return;
    }

    const currentMessage = currentStageMessages[currentMessageIndex];
    if (!currentMessage.isHuman) {
      // Assistant message - show typing indicator first
      setIsAssistantTyping(true);
      setHumanReadTimeDelay(humanReadTimeDelay + currentMessage.text.split(" ").length * 100); // longer AI message = longer time before human response
      let pauseBefore = currentMessage.customPauseBefore || 2000;
      const timer = setTimeout(() => {
        setIsAssistantTyping(false);
        setMessages(prev => [...prev, { ...currentMessage, finishedTyping: true }]);
        setCurrentMessageIndex(prev => prev + 1);
      }, pauseBefore);
      return () => clearTimeout(timer);
    } else {
      // Human message - add to messages immediately with finishedTyping false
      let pauseBefore = currentMessage.customPauseBefore || Math.max(humanReadTimeDelay, 3000);
      const timer = setTimeout(() => {
        setMessages(prev => [...prev, { ...currentMessage, finishedTyping: false }]);
        setHumanReadTimeDelay(0);
      }, pauseBefore);
      return () => clearTimeout(timer);
    }
  }, [currentMessageIndex, currentStage]);

  const handleHumanTypingComplete = () => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage) {
      setMessages(prev => [
        ...prev.slice(0, -1),
        { ...lastMessage, finishedTyping: true }
      ]);
    }
    setIsAssistantTyping(true);
    setCurrentMessageIndex(prev => prev + 1);
  };

  const isDemoComplete = () => { return currentStageIndex + 1 >= totalStages };
  const showSkipLink = () => { return context === TourContext.NewUserTour && !isDemoComplete() };

  const continueButtonText = () => {
    if (isDemoComplete()) {
      if (context === TourContext.NewUserTour) {
        return 'Start Onboarding';
      } else {
        return 'Finish Tour';
      }
    } else {
      return 'Continue';
    }
  }

  const handleContinue = () => {
    if (isDemoComplete()) {
      if (context === TourContext.NewUserTour) {
        window.location.href = '/onboarding';
      } else {
        // Guest tour users should go back to homepage
        window.location.href = '/';
      }
      return;
    }

    const currentStageMessages = DEMO_SCRIPT[currentStage].messages;
    if (currentMessageIndex < currentStageMessages.length - 1) {
      // More messages in current stage
      setCurrentMessageIndex(prev => prev + 1);
    } else {
      // When a stage is completed, add the stage's 'highlights' to the growing list
      const currentStageHighlights = DEMO_SCRIPT[currentStage].highlights;
      setCompletedHighlights(prev => [...prev, ...currentStageHighlights]);

      // Move to next stage
      const nextStageIndex = currentStageIndex + 1;
      if (nextStageIndex < totalStages) {
        setCurrentStage(stages[nextStageIndex]);
        setCurrentMessageIndex(0);
        setMessages([]);
      }
    }
  };

  const currentStageFinalMessageHasFinishedTyping = () => {
    const currentStageMessages = DEMO_SCRIPT[currentStage].messages;
    return messages.length === currentStageMessages.length && messages[messages.length - 1]?.finishedTyping;
  };

  const handleSkip = () => {
    if (context === TourContext.NewUserTour) {
      window.location.href = '/onboarding';
    } else {
      // Guest tour users should go back to homepage
      window.location.href = '/';
    }
  };

  return (
    <div className="demo-tour">
      {!isMobile && (
        <DemoSidebar
          explanation={DEMO_SCRIPT[currentStage].explanation}
          currentStage={currentStageIndex + 1}
          totalStages={totalStages}
          completedHighlights={completedHighlights}
        />
      )}
      <div className="chat-body-container">
        <div className="chat-container">
          <div className="chat-messages">
            {messages.map((message, index) => (
              <div
                key={index}
                style={{
                  position: 'relative',
                  zIndex: message.isHuman ? 10 : 5
                }}
              >
                {message.isHuman ? (
                  <div className="chat-message-container">
                    <TypingMessage
                      text={message.text}
                      onComplete={handleHumanTypingComplete}
                    />
                  </div>
                ) : (
                  <ChatMessage
                    context="demo-tour"
                    message={{
                      raw_text: message.text,
                      author_type: 'assistant',
                      message_type: message.message_type as ChatMessageType['message_type'] || 'message',
                    }}
                  />
                )}
              </div>
            ))}
            {isAssistantTyping && <ChatTypingIndicator />}
            <div ref={messagesEndRef} />
          </div>
          <div className="demo-controls">
            <button
              className="demo-continue-button"
              type="button"
              onClick={handleContinue}
              disabled={!currentStageFinalMessageHasFinishedTyping()}
            >
              {continueButtonText()}
            </button>
            {showSkipLink() && (
              <a
                href="#"
                className="demo-skip-link"
                onClick={(e) => {
                  e.preventDefault();
                  handleSkip();
                }}
              >
                Start Onboarding
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
